import React, { useEffect, useRef, useState } from "react";

import {
    Alert,
    Image,
    Platform,
    Text,
    TouchableOpacity,
    View,
    ViewStyle
} from "react-native";
import Images, { bundleImage } from "../../../specific/utils/Images";
import Divider from "../../designSystem/Divider/Divider";
import User from "../../data/user/User";
import AppTheme from "../../utils/Theme";
import Style from "../../utils/Style";
import Button from "../../designSystem/Button";
import { AppTarget, getAppTarget } from "../../../specific/utils/Navigation/Host";
import { getDocumentUrlForMediaId } from "../../../specific/services/AppWrite/AppWriteDatabase";
import { PDFDocument } from "./SuperRoom";
import { openUrl } from "../../utils/WebLink";

import InViewModal from "../../../specific/components/InViewModal/InViewModal";
import * as Navigation from "../../../specific/utils/Navigation/Navigation";
import SVGView from "../../utils/SvgView";
import { default as IcCheck } from "../../../shared/assets/svg/icons/solid/check.svg";
import ActivityIndicator from "../../designSystem/ActivityIndicator/ActivityIndicator";
import { LaunchStep } from "../../models/Models";
import { getLocalintegrationSteps } from "../../../specific/utils/LocalStorage";
import i18n, { getLanguage, translate } from "../../utils/Localization/Localization";
import PDFViewer from "../../../specific/components/PDF/PDFViewer";
import * as Constants from "../../utils/Constants";
import Modal from "../../../specific/components/Modal/Modal";
import UserLevelSelection from "../Filters/UserLevelSelection";
import { addDataToUser } from "../../services/Firebase/Database";
import DocumentsViewer from "../Documents/DocumentsViewer";

interface StartingStepsProps {
    width: number,
    height: number,
    componentId?: string,
    navigate?: string,
    showAtLaunch?: boolean,
    deeplink?: string,
    designElements: any,
    newsSection: React.JSX.Element
}

type StartingStep = {
    id: string,
    icon: number | bundleImage,
    title: string
}

const iconBySteps: { [key: string]: number } = {
    "level": require("../../assets/img/rooms/ic_filter.jpg"),
    "witness_teacher": require("../../assets/img/rooms/ic_parole-super-prof.jpg"),
    "witness_anim": require("../../assets/img/rooms/ic_parole-super-anim.jpg"),
    "witness_family": require("../../assets/img/rooms/ic_parole-super-parents.jpg"),
    "ic_reportage": require("../../assets/img/rooms/ic_reportage.jpg")
}

const StartingSteps = (props: StartingStepsProps) => {

    const appTheme: AppTheme = new AppTheme();
    const images: Images = new Images();
    const user: User = User.getInstance();
    const inViewModal = useRef<InViewModal>(null);

    const { componentId, navigate, width, height, showAtLaunch = false, deeplink = "", designElements, newsSection } = props;

    const [viewDidAppear, setViewDidAppear] = useState<boolean>(false);
    const [showSteps, setShowSteps] = useState<boolean>(showAtLaunch);
    const [launchSteps, setLaunchSteps] = useState<LaunchStep[] | undefined>(undefined);
    const [pdfDocumentToShow, setPdfDocumentToShow] = useState<PDFDocument | undefined>(undefined);
    const [documentIdToShow, setDocumentIdToShow] = useState<string | undefined>(undefined);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [resfreshView, setRefreshView] = useState<number>(0);

    let backgroundColor: string = appTheme.parentBackgroundColor;
    let stepsColor = appTheme.homeColor;
    let stepsTitleColor = appTheme.darkBlue;
    let macaroonImage = images.icParentMacaroon;
    let itemsColor: string = appTheme.homeColor;
    let itemsTextColor: string = appTheme.darkBlue;
    if (user.profile === "teacher") {
        stepsColor = "#716899";
        stepsTitleColor = "#FFFFFF";
        backgroundColor = appTheme.teacherBackgroundColor;
        macaroonImage = images.icTeacherMacaroon;
        itemsColor = appTheme.schoolColor;
        itemsTextColor = appTheme.white;
    } else if (user.profile === "animator") {
        stepsColor = appTheme.schoolColor;
        stepsTitleColor = "#FFFFFF";
        backgroundColor = appTheme.animatorBackgroundColor;
        macaroonImage = images.icTeacherMacaroon;
        itemsColor = appTheme.schoolColor;
        itemsTextColor = appTheme.white;
    }

    const sectionButtonStyle: ViewStyle = {
        marginTop: appTheme.pixelPerfect(10),
        paddingHorizontal: appTheme.pixelPerfect(20),
        paddingVertical: appTheme.pixelPerfect(5),
        backgroundColor: itemsColor,
        borderRadius: appTheme.pixelPerfect(30)
    };

    useEffect(() => {
        if (launchSteps !== undefined) {
            setViewDidAppear(true);
        }
    }, [launchSteps]);

    const onViewWillAppear = async () => {
        const localLaunchSteps = await getLocalintegrationSteps();
        const localLaunchStepsData = localLaunchSteps.data;
        let updatedLaunchStepsByTarget: LaunchStep[] = [];
        for (const aLocalLaunchStepKey in localLaunchStepsData) {
            if (Object.prototype.hasOwnProperty.call(localLaunchStepsData, aLocalLaunchStepKey)) {
                let aLaunchStep: LaunchStep = localLaunchStepsData[aLocalLaunchStepKey];
                if ((aLaunchStep.target.indexOf(user.profile) !== -1) || ((Platform.OS === "web") && (aLaunchStep.target.indexOf(`web_${user.profile}`) !== -1))) {
                    // On va regarder où le ranger en fonction de l'ordre d'affichage défini en base
                    let launchStepInserted: boolean = false;
                    if (aLaunchStep.order_by_target[user.profile] !== undefined) {
                        for (const anOrderedIndex in updatedLaunchStepsByTarget) {
                            if (Object.prototype.hasOwnProperty.call(updatedLaunchStepsByTarget, anOrderedIndex)) {
                                const anOrderedLaunchStep: LaunchStep = updatedLaunchStepsByTarget[anOrderedIndex];
                                if (anOrderedLaunchStep.order_by_target[user.profile] !== undefined) {
                                    if (aLaunchStep.order_by_target[user.profile] < anOrderedLaunchStep.order_by_target[user.profile]) {
                                        updatedLaunchStepsByTarget.splice(parseInt(anOrderedIndex), 0, aLaunchStep);
                                        launchStepInserted = true;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    if (launchStepInserted === false) {
                        updatedLaunchStepsByTarget.push(aLaunchStep);
                    }
                }
            }
        }
        setLaunchSteps(updatedLaunchStepsByTarget);
    }

    const onCellRequest = async (request: LaunchStep, backgroundColor?: string) => {
        console.log({ request });
        const appTarget: AppTarget = getAppTarget();
        let color: string = appTheme.schoolColor;
        if (user.profile === "family") {
            color = appTheme.homeColor;
        }
        if (backgroundColor === undefined) {
            backgroundColor = color;
        }
        if (request.action.type === "doc") {
            setDocumentIdToShow(request.action.urls[user.profile][getLanguage()]);
        } else if (request.action.type === "pdf") {
            let pdfUrl: string | undefined = undefined;
            let pdfTitle: string = "Lili";
            if (request.action.url !== undefined) {
                pdfUrl = request.action.url[getLanguage()];
            } else if ((request.action.urls !== undefined) && (request.action.urls[user.profile] !== undefined)) {
                pdfUrl = request.action.urls[user.profile][getLanguage()];
            }
            if ((appTarget !== "public") && (request.action.id !== undefined)) {
                pdfUrl = getDocumentUrlForMediaId({ media_id: request.action.id });
            }
            if ((request.action.title !== undefined) && (request.action.title[getLanguage()] !== undefined)) {
                pdfTitle = request.action.title[getLanguage()];
            } else if ((request.action.titles !== undefined) && (request.action.titles[user.profile] !== undefined) && (request.action.titles[user.profile][getLanguage()] !== undefined)) {
                pdfTitle = request.action.titles[user.profile][getLanguage()];
            }
            if ((pdfUrl !== undefined) && (pdfUrl.length > 0)) {
                setPdfDocumentToShow({
                    title: pdfTitle,
                    url: pdfUrl,
                    color
                });
            }
        } else if (request.action.type === "show_filters") {
            setTimeout(() => {
                setShowFilters(true);
            }, 500);
        } else if (request.action.type === "link") {
            if ((request.action.url !== undefined) && (request.action.url[getLanguage()] !== undefined)) {
                openUrl(request.action.url[getLanguage()]);
            } else if ((request.action.urls !== undefined) && (request.action.urls[user.profile] !== undefined) && (request.action.urls[user.profile][getLanguage()] !== undefined)) {
                openUrl(request.action.urls[user.profile][getLanguage()]);
            }
        } else if (request.action.type === "activity") {
            const passProps = {
                activityKey: request.action.activity_key,
                categoryKey: request.action.category_key,
                sectionKey: request.action.section_key,
                context: user.profile,
                isModal: true
            };
            if (Platform.OS === "web") {
                inViewModal.current?.showAlert({
                    name: Constants.ScreenActivitySheet,
                    passProps
                });
            } else {
                Navigation.showModal({
                    componentId,
                    navigate,
                    passProps,
                    name: Constants.ScreenActivitySheet
                })
            }
        }
        if (user.steps !== undefined) {
            if ((user.steps[request.id] === undefined) || ((user.steps[request.id] !== undefined) && (user.steps[request.id] === false))) {
                let userSteps = user.steps;
                userSteps[request.id] = true;
                await addDataToUser({ key: "steps", value: userSteps, user_id: user.uid });
                setRefreshView(new Date().getTime());
            }
        }
    }

    const getDocViewer = () => {
        if (documentIdToShow === undefined) {
            return <View />
        }
        return <DocumentsViewer
            documentId={documentIdToShow}
            onDocumentCloseRequest={() => setDocumentIdToShow(undefined)} />;
    }

    const getPDFViewer = () => {
        if (pdfDocumentToShow === undefined) {
            return <View />
        }
        return <PDFViewer
            title={pdfDocumentToShow.title}
            pdfUrl={pdfDocumentToShow.url}
            color={pdfDocumentToShow.color}
            closeTheDocument={() => setPdfDocumentToShow(undefined)} />
    }

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    }

    const showStepAction = async (step: LaunchStep) => {
        if (step.hide_steps === true) {
            setShowSteps(false);
        }
        onCellRequest(step, backgroundColor);
    }

    const stepCell = (data: { step: LaunchStep, isFirst: boolean, isLast: boolean }) => {
        const { step } = data;
        let isStepChecked = false;
        if (user.steps !== undefined) {
            if (user.steps[step.id] !== undefined) {
                isStepChecked = user.steps[step.id];
            }
        }
        let stepImage: number | { uri: string } = step.icon !== undefined ? images[step.icon] : step.icon_id !== undefined ? iconBySteps[step.icon_id] : iconBySteps[step.icons_id[user.profile]];
        if (step.id === "notice") {
            stepImage = images.illStepNotice;
        }
        const playButtonSize = Platform.OS === "web" ? appTheme.pixelPerfect(42) : appTheme.pixelPerfect(34);
        const lineHeight = Platform.OS === "web" ? appTheme.pixelPerfect(10) : appTheme.pixelPerfect(5);
        const actionButtonBackgroundColor = isStepChecked === false ? appTheme.white : stepsColor;
        const actionButtonTextColor = isStepChecked === true ? stepsTitleColor : appTheme.darkBlue;
        const mediaCellWidth = Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth();
        const checkView = isStepChecked === false ? <View style={{ width: playButtonSize }} /> : <View style={{ width: playButtonSize, justifyContent: "center", alignItems: "center" }}><SVGView Component={IcCheck} color={actionButtonTextColor} size={14} /></View>;
        let stepTitle: string = "";
        if ((step.title !== undefined) && (step.title[getLanguage()] !== undefined)) {
            stepTitle = step.title[getLanguage()];
        } else if ((step.titles !== undefined) && (step.titles[user.profile] !== undefined) && (step.titles[user.profile][getLanguage()] !== undefined)) {
            stepTitle = step.titles[user.profile][getLanguage()];
        }
        return <TouchableOpacity onPress={() => showStepAction(step)} style={{ backgroundColor: appTheme.white, flexDirection: "row", height: appTheme.pixelPerfect(45) + lineHeight, justifyContent: "flex-start", alignItems: "center", width: appTheme.getFlexDirectionForSplitScreen() === "row" ? mediaCellWidth * 0.6 : mediaCellWidth * 0.9 }}>
            <View style={{ borderWidth: appTheme.pixelPerfect(0.5), borderColor: stepsColor + "4D", borderRadius: (playButtonSize + appTheme.pixelPerfect(6)) / 2, backgroundColor: actionButtonBackgroundColor, flexDirection: "row", height: playButtonSize + appTheme.pixelPerfect(6), justifyContent: "flex-start", alignItems: "center", paddingHorizontal: appTheme.pixelPerfect(2), width: appTheme.getFlexDirectionForSplitScreen() === "row" ? mediaCellWidth * 0.6 : mediaCellWidth * 0.9 }}>
                <View style={{ borderWidth: 1, borderColor: appTheme.darkBlue, width: playButtonSize, height: playButtonSize, borderRadius: playButtonSize / 2, justifyContent: "center", alignItems: "center", backgroundColor: actionButtonBackgroundColor, marginEnd: appTheme.pixelPerfect(10) }}>
                    <Image source={stepImage} style={{ width: playButtonSize, height: playButtonSize, borderRadius: playButtonSize / 2 }} />
                </View>
                <Text numberOfLines={3} style={{ color: actionButtonTextColor, flex: 1, fontFamily: appTheme.primarySemiBoldFont, fontSize: appTheme.pixelPerfectForFont(8) }}>
                    {stepTitle}
                </Text>
                {checkView}
            </View>
        </TouchableOpacity>
    }

    const getStepList = () => {
        let stepListView = [];
        for (const aStepIndex: string in launchSteps) {
            if (Object.prototype.hasOwnProperty.call(launchSteps, aStepIndex)) {
                const aStep: LaunchStep = launchSteps[parseInt(aStepIndex)];
                stepListView.push(stepCell({
                    step: aStep,
                    isFirst: parseInt(aStepIndex) === 0,
                    isLast: parseInt(aStepIndex) === launchSteps.length - 1,
                }))
            }
        }
        return stepListView
    }

    const getSteps = () => {
        if (showSteps === false) {
            return <View />
        }

        let modalTitle: string = "Comment intégrer Lili dans le temps de classe ?";
        if (user.profile === "animator") {
            modalTitle = "Comment utiliser Lili en périscolaire ?";
        } else if (user.profile === "family") {
            modalTitle = "Comment utiliser Lili à la maison ?";
        }
        return <View style={{ height: appTheme.getFullAppHeight(), flex: 1, justifyContent: "flex-end", backgroundColor: backgroundColor + "CC" }}>
            <View>
                <View style={[Style.shadowed, { backgroundColor: appTheme.white, borderTopEndRadius: appTheme.pixelPerfect(20), borderTopStartRadius: appTheme.pixelPerfect(20), paddingTop: appTheme.pixelPerfect(40), paddingBottom: appTheme.pixelPerfect(40), alignItems: "center" }]}>
                    <Text style={{ textAlign: "center", textTransform: "uppercase", fontFamily: appTheme.primaryBoldFont, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(7) }}>
                        {modalTitle}
                    </Text>
                    <Divider />
                    {getStepList()}
                    <Divider />
                    <Button onPress={() => setShowSteps(false)} title={i18n.t("startingSteps.modal.button")} link={true} />
                </View>
            </View>
        </View>
    }

    const getStepsSection = () => {
        if (viewDidAppear === false) {
            return <View onLayout={onViewWillAppear} style={{ alignItems: "center", justifyContent: "center", height: appTheme.pixelPerfect(60), width: appTheme.getFullScreenWidth(), flexDirection: "row" }}>
                <ActivityIndicator loading={true} color={appTheme.white} />
            </View>
        }
        const imageWidth: number = width - 20;
        const imageHeight: number = imageWidth * 370 / 828;
        const illuWidth: number = (width - 20) * 2 / 3;
        const illuHeight: number = illuWidth * 473 / 1030;
        const illuTopPosition: number = imageHeight - (illuHeight / 3);
        const icWidth: number = imageWidth / 3;
        const icHeight: number = icWidth * 139 / 366;
        const lineHeight: number = illuWidth * 25 / 1029;
        let backgroundColor: string = "#57537F";
        let textColor: string = appTheme.white;
        let catIllustrationSource: number = require("../../assets/img/rooms/ill_lae_room_teacher.png");
        let photoIllustrationSource: number = require("../../assets/img/rooms/photo_lae_room_teacher.jpg");
        const userProfile: "teacher" | "animator" | "family" = ((user.profile === undefined) || (user.profile === "doctor") || (user.profile === "neutral")) ? "teacher" : user.profile;
        let title: string = translate.labels().rooms.integrate_lili[userProfile].title;
        let chapo: string = translate.labels().rooms.integrate_lili[userProfile].chapo;
        let body: string = translate.labels().rooms.integrate_lili[userProfile].body;
        let ministerLogo: React.JSX.Element = <View />;
        if (user.profile === "family") {
            backgroundColor = "#FFD77A";
            textColor = appTheme.darkBlue;
            catIllustrationSource = require("../../assets/img/rooms/ill_lam_room_family.png");
            photoIllustrationSource = require("../../assets/img/rooms/photo_lam_room_family.png");
        } else if (user.profile === "animator") {
            catIllustrationSource = require("../../assets/img/rooms/ill_lep_room_animator.png");
            photoIllustrationSource = require("../../assets/img/rooms/photo_lep_room_anims.png");
        } else if (user.profile === "teacher") {
            ministerLogo = <Image source={require("../../assets/img/rooms/logo_menj_eduup.png")} style={{ position: "absolute", top: imageHeight - icHeight + 10, left: 10, width: icWidth, height: icHeight, borderBottomLeftRadius: 14 }} />;
        }

        return <View style={{ alignItems: "center", width, padding: 10, backgroundColor, borderRadius: appTheme.pixelPerfect(16), margin: 5 }}>
            <Image source={photoIllustrationSource} style={{ width: imageWidth, height: imageHeight, borderRadius: 14 }} />
            {ministerLogo}
            <Image source={catIllustrationSource} style={{ position: "absolute", top: illuTopPosition, width: illuWidth, height: illuHeight }} />
            <Text style={{ marginTop: illuHeight * 2 / 3, textAlign: "center", fontSize: appTheme.pixelPerfectForFont(12), color: textColor, fontFamily: appTheme.primaryBoldFont, textTransform: "uppercase" }}>
                {title}
            </Text>
            <Image source={require("../../assets/img/rooms/orange_line.png")} style={{ width: illuWidth, height: lineHeight, marginTop: 10 }} />
            <Text style={{ paddingHorizontal: appTheme.pixelPerfect(15), marginTop: 10, textAlign: "center", fontSize: appTheme.pixelPerfectForFont(9), color: textColor, fontFamily: appTheme.primarySemiBoldFont }}>
                {chapo}
            </Text>
            <Text style={{ marginTop: 4, textAlign: "center", fontSize: appTheme.pixelPerfectForFont(8), color: textColor, fontFamily: appTheme.primaryFont }}>
                {body}
            </Text>
            <Button onPress={() => setShowSteps(true)} style={{ backgroundColor: appTheme.talk, width: appTheme.pixelPerfect(130), marginVertical: 20 }} title={"Découvrir"} />
        </View>;
    }

    return <View style={{ width }}>
        {getStepsSection()}
        <Modal visible={showSteps} animationType="slide" transparent={true}>
            {getSteps()}
            <UserLevelSelection
                visible={showFilters}
                style={{ flex: 1, justifyContent: "flex-end" }}
                backgroundColor={itemsColor}
                toggleFilters={toggleFilters} />
            <Modal animationType="slide" visible={documentIdToShow !== undefined} transparent={true} style={{ width: appTheme.getFullScreenWidth() }}>
                <View style={{ flex: 1, alignItems: "center", backgroundColor: designElements.mainBackgroundColor + "80", width: appTheme.getFullScreenWidth() }}>
                    <View style={{ flex: 1, justifyContent: "flex-end", backgroundColor: designElements.mainBackgroundColor + "80", width: appTheme.getFullAppWidth() }}>
                        {getDocViewer()}
                    </View>
                </View>
            </Modal>
            <Modal animationType="slide" visible={pdfDocumentToShow !== undefined} transparent={true} style={{ width: appTheme.getFullScreenWidth() }}>
                <View style={{ flex: 1, alignItems: "center", backgroundColor: designElements.mainBackgroundColor + "80", width: appTheme.getFullScreenWidth() }}>
                    <View style={{ flex: 1, justifyContent: "flex-end", backgroundColor: designElements.mainBackgroundColor + "80", width: appTheme.getFullAppWidth() }}>
                        {getPDFViewer()}
                    </View>
                </View>
            </Modal>
        </Modal>
        <InViewModal ref={inViewModal} />
    </View>

}

export default StartingSteps;